import type { Locale } from '../../i18n/locales';
import type { PageWidget, PageWidgetItem, PageWidgetKey, PageWidgetType } from '../../types/content';

const isPageWidgetKey = (value: string): value is PageWidgetKey => {
  return ['home', 'blogi', 'mediassa', 'kotivakuutus', 'asiakastuki'].includes(value);
};

const isWidgetType = (value: string): value is PageWidgetType => {
  return [
    'feature_cards',
    'split_highlight',
    'metrics_strip',
    'testimonial_cards',
    'resource_list',
    'contact_cards',
    'cta_banner',
    'popular_topics',
  ].includes(value);
};

const normalizeBool = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
};

export const parseWidgetItems = (itemsJson?: string): PageWidgetItem[] => {
  if (!itemsJson?.trim()) return [];

  try {
    const parsed = JSON.parse(itemsJson) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((item) => ({
        title: typeof item.title === 'string' ? item.title : undefined,
        body: typeof item.body === 'string' ? item.body : undefined,
        value: typeof item.value === 'string' ? item.value : undefined,
        label: typeof item.label === 'string' ? item.label : undefined,
        href: typeof item.href === 'string' ? item.href : undefined,
        imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : undefined,
        imageAlt: typeof item.imageAlt === 'string' ? item.imageAlt : undefined,
      }))
      .filter((item) => item.title || item.body || item.value || item.label || item.href);
  } catch {
    return itemsJson
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => ({ title: line }));
  }
};

export const mapRowToPageWidget = (
  row: Record<string, unknown>,
  locale: Locale,
  defaultPageKey: PageWidgetKey,
): PageWidget | null => {
  const rowPageKey = typeof row.page_key === 'string' && isPageWidgetKey(row.page_key) ? row.page_key : defaultPageKey;
  const rowWidgetType = typeof row.widget_type === 'string' && isWidgetType(row.widget_type) ? row.widget_type : 'feature_cards';

  const rawVisibility = row.is_visible;
  const isVisible = rawVisibility === undefined || rawVisibility === null ? true : normalizeBool(rawVisibility);

  return {
    id: (row.id as string | number) ?? `${rowPageKey}-${rowWidgetType}-${String(row.order || 0)}`,
    locale: (typeof row.locale === 'string' ? row.locale : locale) || locale,
    pageKey: rowPageKey,
    widgetType: rowWidgetType,
    title: typeof row.title === 'string' ? row.title : undefined,
    subtitle: typeof row.subtitle === 'string' ? row.subtitle : undefined,
    body: typeof row.body === 'string' ? row.body : undefined,
    badge: typeof row.badge === 'string' ? row.badge : undefined,
    primaryCtaLabel: typeof row.primary_cta_label === 'string' ? row.primary_cta_label : undefined,
    primaryCtaHref: typeof row.primary_cta_href === 'string' ? row.primary_cta_href : undefined,
    secondaryCtaLabel: typeof row.secondary_cta_label === 'string' ? row.secondary_cta_label : undefined,
    secondaryCtaHref: typeof row.secondary_cta_href === 'string' ? row.secondary_cta_href : undefined,
    imageUrl: typeof row.image_url === 'string' ? row.image_url : undefined,
    imageAlt: typeof row.image_alt === 'string' ? row.image_alt : undefined,
    itemsJson: typeof row.items_json === 'string' ? row.items_json : undefined,
    isVisible,
    order: typeof row.order === 'number' ? row.order : Number(row.order || 0),
  };
};
